import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, CheckCircle2, Loader2, Send, Lock } from "lucide-react";
import { authHeaders, errText } from "@/lib/memberAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TEVAL_CRITERIA = [["clarity", "Σαφήνεια εξήγησης"], ["preparation", "Προετοιμασία / Οργάνωση"], ["responsiveness", "Ανταπόκριση σε απορίες"], ["climate", "Κλίμα στην τάξη"], ["overall", "Συνολική εντύπωση"]];
const inp = "w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:outline-none focus:border-primary";

export function Stars({ value, onChange, size = "h-6 w-6", testId }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid={testId} onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={testId ? `${testId}-${n}` : undefined} onClick={() => onChange?.(n)} onMouseEnter={() => onChange && setHover(n)} className={onChange ? "transition-transform hover:scale-110" : "cursor-default"} aria-label={`${n} αστέρια`}>
          <Star className={`${size} ${(hover || value) >= n ? "fill-amber-400 text-amber-400" : "text-border"}`} />
        </button>
      ))}
    </div>
  );
}

function TeacherForm({ teacher, onDone }) {
  const [v, setV] = useState({ clarity: 0, preparation: 0, responsiveness: 0, climate: 0, overall: 0, comment: "", change: "" });
  const [busy, setBusy] = useState(false);
  const missing = TEVAL_CRITERIA.filter(([k]) => !v[k]);
  const submit = async (e) => {
    e.preventDefault();
    if (missing.length) return toast.error("Συμπλήρωσε όλα τα αστέρια.");
    setBusy(true);
    try { await axios.post(`${API}/members/teacher-eval`, { ...v, teacher_id: teacher.id }, authHeaders()); toast.success("Ευχαριστούμε! Η αξιολόγηση καταχωρήθηκε."); onDone(); } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid={`teval-form-${teacher.id}`} className="bg-white border border-border rounded-sm p-5">
      <div className="font-heading font-bold">{teacher.name}</div>
      <div className="text-xs text-muted-foreground mb-4">{teacher.subjects.join(" · ") || "Καθηγητής/τρια"}</div>
      <div className="space-y-3">
        {TEVAL_CRITERIA.map(([k, l]) => (
          <div key={k} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-4">
            <span className={`text-sm ${k === "overall" ? "font-semibold" : ""}`}>{l}</span>
            <Stars value={v[k]} onChange={(n) => setV({ ...v, [k]: n })} testId={`teval-${teacher.id}-${k}`} />
          </div>
        ))}
      </div>
      <textarea data-testid={`teval-${teacher.id}-comment`} className={`${inp} mt-4`} rows={2} placeholder="Σχόλιο (προαιρετικό)" value={v.comment} onChange={(e) => setV({ ...v, comment: e.target.value })} />
      <textarea data-testid={`teval-${teacher.id}-change`} className={`${inp} mt-2`} rows={2} placeholder="Τι θα ήθελες να αλλάξει; (προαιρετικό)" value={v.change} onChange={(e) => setV({ ...v, change: e.target.value })} />
      <button data-testid={`teval-${teacher.id}-submit`} disabled={busy} className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-sm bg-primary text-primary-foreground font-mono-accent text-[10px] uppercase tracking-widest hover:bg-accent disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Υποβολή αξιολόγησης</button>
    </form>
  );
}

export default function TeacherEvalForm() {
  const [state, setState] = useState(null);
  const load = () => axios.get(`${API}/members/teacher-eval`, authHeaders()).then((r) => setState(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);

  if (!state) return <div className="p-6 text-sm text-muted-foreground">Φόρτωση…</div>;
  const pending = state.teachers.filter((t) => !t.done);
  return (
    <div data-testid="teval" className="space-y-4">
      <div className="bg-white border border-border rounded-sm p-5">
        <h3 className="font-heading font-bold text-lg">Αξιολόγηση καθηγητών</h3>
        <p className="text-sm text-muted-foreground mt-1">Η γνώμη σου βοηθά το φροντιστήριο να γίνεται καλύτερο. Τις απαντήσεις βλέπει μόνο η διεύθυνση — όχι οι καθηγητές.</p>
        {state.open && state.period
          ? <div data-testid="teval-period" className="mt-3 inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-200 px-3 py-1.5 rounded-sm">Ανοιχτή περίοδος: {state.period}</div>
          : <div data-testid="teval-closed" className="mt-3 inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-widest bg-secondary text-muted-foreground px-3 py-1.5 rounded-sm"><Lock className="h-3 w-3" /> Δεν υπάρχει ανοιχτή περίοδος αξιολόγησης αυτή τη στιγμή</div>}
      </div>
      {state.open && state.period && <>
        {state.teachers.length === 0 && <p data-testid="teval-no-teachers" className="text-sm text-muted-foreground p-6 bg-white border border-border rounded-sm">Δεν σου έχουν ανατεθεί καθηγητές ακόμη.</p>}
        {pending.map((t) => <TeacherForm key={t.id} teacher={t} onDone={load} />)}
        {state.teachers.filter((t) => t.done).map((t) => (
          <div key={t.id} data-testid={`teval-done-${t.id}`} className="bg-white border border-border rounded-sm p-4 flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /><div><div className="font-medium text-sm">{t.name}</div><div className="text-xs text-muted-foreground">Υποβλήθηκε για την περίοδο «{state.period}». Ευχαριστούμε!</div></div></div>
        ))}
      </>}
    </div>
  );
}
